import { useState, useContext, useEffect } from 'react';
import {
  Box, Typography, TextField, Button, Divider, Alert, Snackbar,
  Paper, Avatar, InputAdornment, IconButton, CircularProgress,
  Grid, Chip
} from '@mui/material';
import Visibility        from '@mui/icons-material/Visibility';
import VisibilityOff     from '@mui/icons-material/VisibilityOff';
import PersonIcon        from '@mui/icons-material/Person';
import LockIcon          from '@mui/icons-material/Lock';
import HomeIcon          from '@mui/icons-material/Home';
import BadgeIcon         from '@mui/icons-material/Badge';
import CheckCircleIcon   from '@mui/icons-material/CheckCircle';
import WarningAmberIcon  from '@mui/icons-material/WarningAmber';
import { useNavigate }   from 'react-router-dom';
import { AuthContext }   from '../context/AuthContext';
import api               from '../api/api';

// ─── Section wrapper ──────────────────────────────────────────────────────────
const Section = ({ icon, title, children }) => (
  <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, mb: 3 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
      <Box sx={{ color: '#163244' }}>{icon}</Box>
      <Typography variant="h6" fontWeight={600} sx={{ color: '#163244' }}>
        {title}
      </Typography>
    </Box>
    <Divider sx={{ mb: 2.5 }} />
    {children}
  </Paper>
);

// ─── Chip de verificación ─────────────────────────────────────────────────────
const VerifiedChip = ({ verified, label }) =>
  verified ? (
    <Chip
      icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
      label={`${label} verificado`}
      size="small"
      sx={{ backgroundColor: '#EAF3DE', color: '#3B6D11', fontWeight: 500, fontSize: 11 }}
    />
  ) : (
    <Chip
      icon={<WarningAmberIcon sx={{ fontSize: 14 }} />}
      label={`${label} sin verificar`}
      size="small"
      sx={{ backgroundColor: '#FAEEDA', color: '#854F0B', fontWeight: 500, fontSize: 11 }}
    />
  );

// ─── Componente principal ─────────────────────────────────────────────────────
export default function ProfilePage() {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [profileFetching, setProfileFetching] = useState(true);
  const [profileLoading, setProfileLoading]   = useState(false);
  const [fieldErrors, setFieldErrors]         = useState({});

  // Datos personales
  const [profileData, setProfileData] = useState({
    // Identidad
    first_name: '',
    last_name:  '',
    username:   '',
    // Contacto
    phone:      '',
    // Documento
    dni:        '',
    birthdate:  '',
    // Domicilio
    street:      '',
    city:        '',
    province:    '',
    postal_code: '',
  });

  // Estado de verificación (solo lectura)
  const [verification, setVerification] = useState({
    email_verified: false,
    phone_verified: false,
  });

  // Contraseña
  const [passData, setPassData]   = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [passLoading, setPassLoading] = useState(false);
  const [showPass, setShowPass]   = useState({ current: false, new: false, confirm: false });
  const [passError, setPassError] = useState('');

  // ─── Fetch inicial ──────────────────────────────────────────────────────────
  useEffect(() => {
    api.get('/users/me')
      .then(res => {
        const d = res.data.user || res.data;
        setProfileData({
          first_name:  d.first_name  || '',
          last_name:   d.last_name   || '',
          username:    d.username    || '',
          phone:       d.phone       || '',
          dni:         d.dni         || '',
          birthdate:   d.birthdate   ? d.birthdate.split('T')[0] : '',
          street:      d.street      || '',
          city:        d.city        || '',
          province:    d.province    || '',
          postal_code: d.postal_code || '',
        });
        setVerification({
          email_verified: !!d.email_verified,
          phone_verified: !!d.phone_verified,
        });
      })
      .catch(() => {
        // Fallback a datos del contexto si el endpoint falla
        if (user) {
          setProfileData(p => ({ ...p, first_name: user.name || '' }));
        }
      })
      .finally(() => setProfileFetching(false));
  }, []);

  const showSnack = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  const field = (key, label, extra = {}) => (
    <TextField
      fullWidth
      size="small"
      label={label}
      value={profileData[key]}
      onChange={e => setProfileData(p => ({ ...p, [key]: e.target.value }))}
      sx={{ mb: 2 }}
      {...extra}
    />
  );

  // ─── Guardar datos personales ───────────────────────────────────────────────
  const handleProfileSave = async () => {
    setFieldErrors({});
    if (!profileData.first_name.trim()) {
      showSnack('El nombre es obligatorio.', 'warning');
      return;
    }
    setProfileLoading(true);
    try {
      await api.put('/users/me', profileData);
      showSnack('Datos actualizados correctamente.');
    } catch (err) {
      const status  = err.response?.status;
      const message = err.response?.data?.error || 'Error al actualizar los datos.';
      // 409 = username ya en uso → marcar el campo específico
      if (status === 409 && message.toLowerCase().includes('usuario')) {
        setFieldErrors({ username: 'Este nombre de usuario ya está en uso. Elegí otro.' });
      } else {
        showSnack(message, 'error');
      }
    } finally {
      setProfileLoading(false);
    }
  };

  // ─── Cambiar contraseña ─────────────────────────────────────────────────────
  const handlePasswordChange = async () => {
    setPassError('');
    const { currentPassword, newPassword, confirmPassword } = passData;

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPassError('Completá todos los campos.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPassError('Las contraseñas nuevas no coinciden.');
      return;
    }
    const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;
    if (!regex.test(newPassword)) {
      setPassError('Mínimo 8 caracteres con mayúsculas, minúsculas, números y símbolos.');
      return;
    }

    setPassLoading(true);
    try {
      await api.put('/users/change-password', { currentPassword, newPassword });
      setPassData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      showSnack('Contraseña actualizada. Cerrando sesión...');
      setTimeout(() => { logout(); navigate('/'); }, 2000);
    } catch (err) {
      setPassError(err.response?.data?.error || 'Error al cambiar la contraseña.');
    } finally {
      setPassLoading(false);
    }
  };

  const passField = (key, label, showKey) => (
    <TextField
      fullWidth size="small" label={label}
      type={showPass[showKey] ? 'text' : 'password'}
      value={passData[key]}
      onChange={e => setPassData(p => ({ ...p, [key]: e.target.value }))}
      sx={{ mb: 2 }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton size="small" onClick={() => setShowPass(p => ({ ...p, [showKey]: !p[showKey] }))}>
              {showPass[showKey] ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
            </IconButton>
          </InputAdornment>
        )
      }}
    />
  );

  // Iniciales para el avatar
  const initials = [profileData.first_name, profileData.last_name]
    .filter(Boolean)
    .map(n => n[0])
    .join('')
    .toUpperCase() || user?.name?.[0]?.toUpperCase() || '?';

  const fullName = [profileData.first_name, profileData.last_name].filter(Boolean).join(' ')
    || user?.name || '';

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ maxWidth: 680, mx: 'auto', px: 2, py: 4 }}>

      {/* Header ──────────────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <Avatar sx={{ width: 64, height: 64, backgroundColor: '#163244', fontSize: 22, fontWeight: 700 }}>
          {initials}
        </Avatar>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ color: '#163244' }}>
            {fullName || 'Tu perfil'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
            {user?.email}
            {user?.role === 'admin' && ' · Administrador'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <VerifiedChip verified={verification.email_verified} label="Email" />
            <VerifiedChip verified={verification.phone_verified} label="Teléfono" />
          </Box>
        </Box>
      </Box>

      {profileFetching ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress sx={{ color: '#163244' }} />
        </Box>
      ) : (
        <>
          {/* Identidad ──────────────────────────────────────────────────── */}
          <Section icon={<PersonIcon />} title="Datos personales">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                {field('first_name', 'Nombre')}
              </Grid>
              <Grid item xs={12} sm={6}>
                {field('last_name', 'Apellido')}
              </Grid>
            </Grid>

            {field('username', 'Nombre de usuario', {
              helperText: fieldErrors.username || 'Podés usarlo para iniciar sesión en lugar del email.',
              error: !!fieldErrors.username,
              onChange: e => {
                setProfileData(p => ({ ...p, username: e.target.value }));
                if (fieldErrors.username) setFieldErrors(p => ({ ...p, username: '' }));
              },
              inputProps: { autoComplete: 'username' }
            })}

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                {field('dni', 'DNI / CUIL')}
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth size="small" label="Fecha de nacimiento"
                  type="date"
                  value={profileData.birthdate}
                  onChange={e => setProfileData(p => ({ ...p, birthdate: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  sx={{ mb: 2 }}
                />
              </Grid>
            </Grid>

            <TextField
              fullWidth size="small" label="Teléfono"
              value={profileData.phone}
              onChange={e => setProfileData(p => ({ ...p, phone: e.target.value }))}
              sx={{ mb: 2 }}
              helperText="Código de área sin 0 + número. Ej: 11 5555-1234"
            />

            <TextField
              fullWidth size="small" label="Email"
              value={user?.email || ''}
              disabled
              helperText="El email no puede modificarse desde aquí."
              sx={{ mb: 2.5 }}
            />

            <Button
              variant="contained"
              onClick={handleProfileSave}
              disabled={profileLoading}
              sx={{ backgroundColor: '#E7A432', color: '#163244', fontWeight: 700,
                    '&:hover': { backgroundColor: '#c98d1f' } }}
            >
              {profileLoading ? <CircularProgress size={20} /> : 'Guardar cambios'}
            </Button>
          </Section>

          {/* Domicilio ───────────────────────────────────────────────────── */}
          <Section icon={<HomeIcon />} title="Domicilio">
            {field('street', 'Calle y número', { helperText: 'Ej: Av. Corrientes 1234, Piso 3 A' })}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                {field('city', 'Localidad / Ciudad')}
              </Grid>
              <Grid item xs={12} sm={6}>
                {field('province', 'Provincia')}
              </Grid>
            </Grid>
            {field('postal_code', 'Código postal')}

            <Button
              variant="contained"
              onClick={handleProfileSave}
              disabled={profileLoading}
              sx={{ backgroundColor: '#E7A432', color: '#163244', fontWeight: 700,
                    '&:hover': { backgroundColor: '#c98d1f' } }}
            >
              {profileLoading ? <CircularProgress size={20} /> : 'Guardar domicilio'}
            </Button>
          </Section>

          {/* Cambio de contraseña ────────────────────────────────────────── */}
          <Section icon={<LockIcon />} title="Cambiar contraseña">
            {passError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPassError('')}>
                {passError}
              </Alert>
            )}
            {passField('currentPassword', 'Contraseña actual',          'current')}
            {passField('newPassword',     'Nueva contraseña',           'new')}
            {passField('confirmPassword', 'Confirmar nueva contraseña', 'confirm')}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Mínimo 8 caracteres, con mayúsculas, minúsculas, números y símbolos.
            </Typography>
            <Button
              variant="contained"
              onClick={handlePasswordChange}
              disabled={passLoading}
              sx={{ backgroundColor: '#163244', color: '#fff', fontWeight: 700,
                    '&:hover': { backgroundColor: '#0e2233' } }}
            >
              {passLoading
                ? <CircularProgress size={20} sx={{ color: '#fff' }} />
                : 'Cambiar contraseña'}
            </Button>
          </Section>
        </>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
